import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
/**
 * SurveyTemplatesEditor — visual CRUD for `customer_survey_templates`.
 *
 * Opens as a modal from SurveysList. Lets admins:
 *   • Browse all active templates (system + custom)
 *   • Duplicate the system default as a starting point
 *   • Add/edit/remove sections, add/edit/remove questions per section
 *   • Change question type (rating / yes_no / text), label, help text, required
 *   • Persist via PUT /api/customer-survey-templates/{id}
 *
 * Notes
 * ─────
 * • The seeded "system-default-survey-en" template can be edited (the
 *   backend doesn't block edits on `is_system`), only deletion is blocked.
 * • Keys auto-derive from labels but are editable (stable IDs are
 *   important for cross-survey reporting once answers exist).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Plus, Trash2, Copy, ChevronUp, ChevronDown, FileText, Save, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate a slug-key from a label. Stable enough for human-readable IDs.
const slugKey = (s) => (s || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 60) || 'question';

const QUESTION_TYPES = [
  { value: 'rating', label: i18n.t('auto.lbl.rating15') },
  { value: 'yes_no', label: i18n.t('auto.lbl.yesNo') },
  { value: 'text', label: i18n.t('auto.lbl.freeText') },
];

export default function SurveyTemplatesEditor({ onClose, onChanged }) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState(null);  // working copy of active template
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    const r = await axios.get(`${API}/customer-survey-templates`);
    setTemplates(r.data || []);
    if (!activeId && r.data?.length) {
      const sys = r.data.find((t) => t.is_system) || r.data[0];
      setActiveId(sys.id);
      setDraft(JSON.parse(JSON.stringify(sys)));
      setDirty(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const selectTemplate = (id) => {
    if (dirty && !window.confirm(t('auto.set.discardUnsavedChanges'))) return;
    const tItem = templates.find((x) => x.id === id);
    setActiveId(id);
    setDraft(tItem ? JSON.parse(JSON.stringify(tItem)) : null);
    setDirty(false);
  };

  const patch = (mutator) => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d));
      mutator(next);
      return next;
    });
    setDirty(true);
  };

  // ----- Section operations -----
  const addSection = () => patch((d) => {
    d.sections.push({
      key: `section_${d.sections.length + 1}`,
      label: t('auto.lbl.newSection'),
      description: '',
      questions: [],
    });
  });

  const removeSection = (idx) => {
    if (!window.confirm(t('auto.set.removeThisSectionAndAllIts'))) return;
    patch((d) => { d.sections.splice(idx, 1); });
  };

  const moveSection = (idx, delta) => patch((d) => {
    const tgt = idx + delta;
    if (tgt < 0 || tgt >= d.sections.length) return;
    const [it] = d.sections.splice(idx, 1);
    d.sections.splice(tgt, 0, it);
  });

  // ----- Question operations -----
  const addQuestion = (sidx) => patch((d) => {
    d.sections[sidx].questions.push({
      key: `q_${d.sections[sidx].questions.length + 1}`,
      label: t('auto.lbl.newQuestion'),
      type: 'rating',
      required: true,
      help_text: '',
    });
  });

  const removeQuestion = (sidx, qidx) => patch((d) => {
    d.sections[sidx].questions.splice(qidx, 1);
  });

  const moveQuestion = (sidx, qidx, delta) => patch((d) => {
    const list = d.sections[sidx].questions;
    const tgt = qidx + delta;
    if (tgt < 0 || tgt >= list.length) return;
    const [it] = list.splice(qidx, 1);
    list.splice(tgt, 0, it);
  });

  // ----- Validation -----
  const validation = useMemo(() => {
    if (!draft) return { ok: true, errors: [] };
    const errors = [];
    if (!draft.name?.trim()) errors.push('Template name is required');
    const sectionKeys = new Set();
    draft.sections.forEach((s, i) => {
      if (!s.key?.trim()) errors.push(`Section ${i + 1}: key is required`);
      if (sectionKeys.has(s.key)) errors.push(`Section ${i + 1}: duplicate key "${s.key}"`);
      sectionKeys.add(s.key);
      const qKeys = new Set();
      (s.questions || []).forEach((q, j) => {
        if (!q.key?.trim()) errors.push(`Section "${s.label}" Q${j + 1}: key required`);
        if (qKeys.has(q.key)) errors.push(`Section "${s.label}": duplicate question key "${q.key}"`);
        qKeys.add(q.key);
        if (!q.label?.trim()) errors.push(`Section "${s.label}" Q${j + 1}: label required`);
      });
    });
    return { ok: errors.length === 0, errors };
  }, [draft]);

  // ----- Save / Duplicate / Delete -----
  const save = async () => {
    if (!validation.ok) {
      toast.error(validation.errors[0]);
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: draft.name,
        language: draft.language || 'en',
        description: draft.description || '',
        sections: draft.sections,
        intro_text: draft.intro_text || '',
      };
      await axios.put(`${API}/customer-survey-templates/${draft.id}`, body);
      toast.success(t('auto.set.templateSaved'));
      setDirty(false);
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async () => {
    if (!draft) return;
    const newName = window.prompt('Name for the copy?', `${draft.name} (copy)`);
    if (!newName) return;
    try {
      const body = {
        name: newName,
        language: draft.language || 'en',
        description: draft.description || '',
        sections: draft.sections,
        intro_text: draft.intro_text || '',
      };
      const r = await axios.post(`${API}/customer-survey-templates`, body);
      toast.success(t('auto.set.templateDuplicated'));
      const newId = r.data.id;
      await reload();
      setActiveId(newId);
      setDirty(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Duplicate failed'); }
  };

  const deleteTpl = async () => {
    if (!draft) return;
    if (draft.is_system) {
      toast.error(t('auto.set.systemTemplatesCanBeEditedBut'));
      return;
    }
    if (!window.confirm(t('auto.set.archiveThisTemplateItWillBe'))) return;
    try {
      await axios.delete(`${API}/customer-survey-templates/${draft.id}`);
      toast.success(t('auto.set.templateArchived'));
      setActiveId(null);
      setDraft(null);
      await reload();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const createBlank = async () => {
    const name = window.prompt('Name the new template:', 'Custom survey');
    if (!name) return;
    try {
      const r = await axios.post(`${API}/customer-survey-templates`, {
        name, language: 'en', sections: [],
      });
      await reload();
      setActiveId(r.data.id);
      setDirty(false);
      toast.success(t('auto.set.emptyTemplateCreatedAddSectionsTo'));
    } catch (e) { toast.error(t('auto.set.createFailed')); }
  };

  return (
    <Dialog open onOpenChange={() => {
      if (dirty && !window.confirm(t('auto.set.discardUnsavedChanges'))) return;
      onClose();
    }}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="survey-templates-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" /> {t('auto.set.customerSurveyTemplates')}
          </DialogTitle>
          <DialogDescription>
            Edit the sections and questions used by your customer surveys.
            Changes here affect new surveys only — surveys already sent keep
            their original questions.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-12 gap-4">
          {/* Left rail: template list */}
          <div className="col-span-4 border-r pr-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold text-slate-600">{t('auto.set.templates')}</Label>
              <Button size="sm" variant="ghost" onClick={createBlank}
                data-testid="survey-tpl-new-blank" className="h-7 px-2 text-xs">
                <Plus className="h-3 w-3 mr-1" /> {t('auto.set.new')}
              </Button>
            </div>
            <div className="space-y-1">
              {templates.map((tItem) => (
                <button key={tItem.id} onClick={() => selectTemplate(tItem.id)}
                  data-testid={`survey-tpl-pick-${tItem.id}`}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm transition ${
                    activeId === tItem.id ? 'bg-indigo-100 text-indigo-900' : 'hover:bg-slate-100'
                  }`}>
                  <div className="font-medium truncate">{tItem.name}</div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
                    {tItem.is_system && <Badge variant="outline" className="text-[9px] h-4 px-1">system</Badge>}
                    {tItem.sections?.length || 0} sections ·{' '}
                    {(tItem.sections || []).reduce((n, s) => n + (s.questions?.length || 0), 0)} questions
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Right pane: editor */}
          <div className="col-span-8 space-y-3">
            {!draft ? (
              <div className="text-sm text-slate-400 italic py-12 text-center">
                Pick a template from the left, or click &quot;New&quot; to start fresh.
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{t('auto.set.name')}</Label>
                    <Input value={draft.name || ''}
                      onChange={(e) => patch((d) => { d.name = e.target.value; })}
                      data-testid="survey-tpl-name" />
                  </div>
                  <div>
                    <Label className="text-xs">{t('auto.set.language')}</Label>
                    <Select value={draft.language || 'en'}
                      onValueChange={(v) => patch((d) => { d.language = v; })}>
                      <SelectTrigger data-testid="survey-tpl-lang"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">{t('auto.set.english')}</SelectItem>
                        <SelectItem value="pl">{t('auto.set.polish')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{t('auto.set.introTextShownToCustomerAbove')}</Label>
                  <Textarea rows={2} value={draft.intro_text || ''}
                    onChange={(e) => patch((d) => { d.intro_text = e.target.value; })}
                    data-testid="survey-tpl-intro" />
                </div>

                {/* Sections */}
                <div className="border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-semibold">Sections ({draft.sections.length})</Label>
                    <Button size="sm" onClick={addSection} data-testid="survey-tpl-add-section">
                      <Plus className="h-3 w-3 mr-1" /> {t('auto.set.addSection2')}
                    </Button>
                  </div>

                  {draft.sections.length === 0 && (
                    <div className="text-xs text-slate-400 italic py-3 text-center border rounded">
                      {t('auto.set.noSectionsYetAddOneTo')}
                    </div>
                  )}

                  <div className="space-y-3">
                    {draft.sections.map((sec, sidx) => (
                      <SectionEditor key={sidx} section={sec} sidx={sidx}
                        onChange={(updates) => patch((d) => { Object.assign(d.sections[sidx], updates); })}
                        onChangeQuestion={(qidx, updates) => patch((d) => {
                          Object.assign(d.sections[sidx].questions[qidx], updates);
                        })}
                        onMove={(delta) => moveSection(sidx, delta)}
                        onRemove={() => removeSection(sidx)}
                        onAddQuestion={() => addQuestion(sidx)}
                        onMoveQuestion={(qidx, delta) => moveQuestion(sidx, qidx, delta)}
                        onRemoveQuestion={(qidx) => removeQuestion(sidx, qidx)}
                        canMoveUp={sidx > 0}
                        canMoveDown={sidx < draft.sections.length - 1}
                      />
                    ))}
                  </div>
                </div>

                {/* Validation messages */}
                {!validation.ok && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
                    <strong>{t('auto.set.fixBeforeSaving')}</strong>
                    <ul className="list-disc ml-5 mt-1">
                      {validation.errors.slice(0, 4).map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <div className="flex gap-2">
            {draft && (
              <>
                <Button size="sm" variant="outline" onClick={duplicate}
                  data-testid="survey-tpl-duplicate" disabled={saving}>
                  <Copy className="h-3 w-3 mr-1" /> {t('auto.set.duplicate')}
                </Button>
                {!draft.is_system && (
                  <Button size="sm" variant="outline" onClick={deleteTpl}
                    data-testid="survey-tpl-delete" disabled={saving}
                    className="text-red-600 hover:bg-red-50">
                    <Trash2 className="h-3 w-3 mr-1" /> {t('auto.set.archive')}
                  </Button>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="dialog-close-btn">
              <X className="h-3 w-3 mr-1" /> {t('auto.set.close')}
            </Button>
            {draft && (
              <Button onClick={save} disabled={saving || !dirty || !validation.ok}
                data-testid="survey-tpl-save">
                <Save className="h-3 w-3 mr-1" /> {saving ? t('auto.set.saving2') : t('auto.set.saveChanges2')}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Section editor ─────────────────────────────────────────────────
function SectionEditor({
  section, sidx, onChange, onChangeQuestion,
  onMove, onRemove, onAddQuestion, onMoveQuestion, onRemoveQuestion,
  canMoveUp, canMoveDown,
}) {
  const { t } = useTranslation();
  return (
    <div className="border rounded-lg bg-slate-50 p-3" data-testid={`survey-tpl-section-${sidx}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 grid grid-cols-12 gap-2">
          <div className="col-span-7">
            <Label className="text-[10px] text-slate-500">{t('auto.set.sectionLabel')}</Label>
            <Input className="h-8 text-sm font-semibold" value={section.label || ''}
              onChange={(e) => onChange({
                label: e.target.value,
                // Auto-derive key as long as it tracks the current label
                // (i.e. user hasn't manually overridden the key field).
                ...((!section.key
                     || section.key === `section_${sidx + 1}`
                     || section.key === slugKey(section.label))
                  ? { key: slugKey(e.target.value) || `section_${sidx + 1}` }
                  : {}),
              })}
              data-testid={`survey-tpl-section-label-${sidx}`} />
          </div>
          <div className="col-span-5">
            <Label className="text-[10px] text-slate-500">{t('auto.set.keyStableId')}</Label>
            <Input className="h-8 text-xs font-mono" value={section.key || ''}
              onChange={(e) => onChange({ key: e.target.value })}
              data-testid={`survey-tpl-section-key-${sidx}`} />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Button size="icon" variant="ghost" className="h-6 w-6"
            disabled={!canMoveUp} onClick={() => onMove(-1)}
            data-testid={`survey-tpl-section-up-${sidx}`}>
            <ChevronUp className="h-3 w-3" />
          </Button>
          <Button size="icon" variant="ghost" className="h-6 w-6"
            disabled={!canMoveDown} onClick={() => onMove(1)}
            data-testid={`survey-tpl-section-down-${sidx}`}>
            <ChevronDown className="h-3 w-3" />
          </Button>
          <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500"
            onClick={onRemove} data-testid={`survey-tpl-section-del-${sidx}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      <div className="mt-2">
        <Label className="text-[10px] text-slate-500">{t('auto.set.descriptionOptional')}</Label>
        <Input className="h-7 text-xs" value={section.description || ''}
          onChange={(e) => onChange({ description: e.target.value })}
          data-testid={`survey-tpl-section-desc-${sidx}`} />
      </div>

      {/* Questions */}
      <div className="mt-3 space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-slate-600">
            Questions ({(section.questions || []).length})
          </Label>
          <Button size="sm" variant="outline" onClick={onAddQuestion}
            data-testid={`survey-tpl-add-q-${sidx}`} className="h-6 text-[11px]">
            <Plus className="h-3 w-3 mr-1" /> {t('auto.set.addQuestion')}
          </Button>
        </div>
        {(section.questions || []).map((q, qidx) => (
          <QuestionEditor key={qidx} q={q} sidx={sidx} qidx={qidx}
            onChange={(u) => onChangeQuestion(qidx, u)}
            onMove={(delta) => onMoveQuestion(qidx, delta)}
            onRemove={() => onRemoveQuestion(qidx)}
            canMoveUp={qidx > 0}
            canMoveDown={qidx < section.questions.length - 1}
          />
        ))}
      </div>
    </div>
  );
}


// ─── Question editor ────────────────────────────────────────────────
function QuestionEditor({ q, sidx, qidx, onChange, onMove, onRemove, canMoveUp, canMoveDown }) {
  const { t } = useTranslation();
  return (
    <div className="bg-white border rounded p-2 grid grid-cols-12 gap-2 items-start"
      data-testid={`survey-tpl-q-${sidx}-${qidx}`}>
      <div className="col-span-5">
        <Label className="text-[10px] text-slate-500">{t('auto.set.label')}</Label>
        <Input className="h-7 text-xs" value={q.label || ''}
          onChange={(e) => onChange({
            label: e.target.value,
            ...((!q.key
                 || q.key === `q_${qidx + 1}`
                 || q.key === slugKey(q.label))
              ? { key: slugKey(e.target.value) || `q_${qidx + 1}` }
              : {}),
          })}
          data-testid={`survey-tpl-q-label-${sidx}-${qidx}`} />
      </div>
      <div className="col-span-3">
        <Label className="text-[10px] text-slate-500">{t('auto.set.key')}</Label>
        <Input className="h-7 text-xs font-mono" value={q.key || ''}
          onChange={(e) => onChange({ key: e.target.value })}
          data-testid={`survey-tpl-q-key-${sidx}-${qidx}`} />
      </div>
      <div className="col-span-2">
        <Label className="text-[10px] text-slate-500">{t('auto.set.type')}</Label>
        <Select value={q.type || 'rating'} onValueChange={(v) => onChange({ type: v })}>
          <SelectTrigger className="h-7 text-xs" data-testid={`survey-tpl-q-type-${sidx}-${qidx}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {QUESTION_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-1 flex flex-col items-center pt-3">
        <Label className="text-[10px] text-slate-500">{t('auto.set.req2')}</Label>
        <input type="checkbox" checked={q.required !== false}
          onChange={(e) => onChange({ required: e.target.checked })}
          data-testid={`survey-tpl-q-req-${sidx}-${qidx}`} />
      </div>
      <div className="col-span-1 flex flex-col gap-0.5 pt-3">
        <Button size="icon" variant="ghost" className="h-5 w-5"
          disabled={!canMoveUp} onClick={() => onMove(-1)}
          data-testid={`survey-tpl-q-up-${sidx}-${qidx}`}>
          <ChevronUp className="h-3 w-3" />
        </Button>
        <Button size="icon" variant="ghost" className="h-5 w-5"
          disabled={!canMoveDown} onClick={() => onMove(1)}
          data-testid={`survey-tpl-q-down-${sidx}-${qidx}`}>
          <ChevronDown className="h-3 w-3" />
        </Button>
        <Button size="icon" variant="ghost" className="h-5 w-5 text-red-500"
          onClick={onRemove}
          data-testid={`survey-tpl-q-del-${sidx}-${qidx}`}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      <div className="col-span-12">
        <Input className="h-6 text-[11px]" placeholder={t('auto.set.helpTextOptional')}
          value={q.help_text || ''}
          onChange={(e) => onChange({ help_text: e.target.value })}
          data-testid={`survey-tpl-q-help-${sidx}-${qidx}`} />
      </div>
    </div>
  );
}
